import { useState, useEffect } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import { StoreHero } from './components/StoreHero';
import { StoreProductGrid } from './components/StoreProductGrid';
import { CartDrawer } from './components/CartDrawer';
import type { StoreProduct } from '../../mocks/storeProducts';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://businessdevelopmentauthority.net';

export interface CartItem extends StoreProduct {
  cartId: string;
  quantity: number;
}

const CART_KEY = 'jl_store_cart';
const PAYMENT_LINK = 'https://e001e9ac-68a7-4c40-b8ea-95640b3298a4.paylinks.godaddy.com/afa25c0a-1b07-44ce-adb7-5b5';

const StorePage: React.FC = () => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [addedMsg, setAddedMsg] = useState('');

  // Load cart from storage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CART_KEY);
      if (stored) setCart(JSON.parse(stored));
    } catch (_) { /* ignore */ }
  }, []);

  const saveCart = (updated: CartItem[]) => {
    setCart(updated);
    localStorage.setItem(CART_KEY, JSON.stringify(updated));
  };

  const addToCart = (product: StoreProduct) => {
    const existing = cart.find(i => i.id === product.id);
    let updated: CartItem[];
    if (existing) {
      updated = cart.map(i =>
        i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i,
      );
    } else {
      updated = [...cart, { ...product, cartId: `${product.id}_${Date.now()}`, quantity: 1 }];
    }
    saveCart(updated);
    setAddedMsg(product.title);
    setTimeout(() => setAddedMsg(''), 2500);
  };

  const removeFromCart = (cartId: string) => {
    saveCart(cart.filter(i => i.cartId !== cartId));
  };

  const changeQty = (cartId: string, qty: number) => {
    if (qty <= 0) return removeFromCart(cartId);
    saveCart(cart.map(i => (i.cartId === cartId ? { ...i, quantity: qty } : i)));
  };

  const totalItems = cart.reduce((s, i) => s + i.quantity, 0);

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/store`,
        'url': `${SITE_URL}/store`,
        'name': 'Book Store - Business Development Authority',
        'description': 'Shop books by Jeffery Long on AI, trading bots, finance, business strategy, personal growth, and real estate. Available as Kindle downloads.',
        'isPartOf': { '@id': SITE_URL },
      },
      {
        '@type': 'Store',
        'name': 'Business Development Authority Book Store',
        'url': `${SITE_URL}/store`,
        'description': 'Official book store featuring titles by Jeffery Long covering AI, finance, business development, real estate, and personal growth.',
        'seller': {
          '@type': 'Organization',
          'name': 'Business Development Authority',
          'url': SITE_URL,
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <div className="min-h-screen bg-white flex flex-col">
        <Header />

        {/* Sticky cart button */}
        <div className="fixed bottom-6 right-6 z-30">
          <button
            onClick={() => setCartOpen(true)}
            className="relative bg-gray-900 text-white w-14 h-14 rounded-full flex items-center justify-center shadow-xl hover:bg-gray-800 transition-colors cursor-pointer"
          >
            <div className="w-7 h-7 flex items-center justify-center">
              <i className="ri-shopping-bag-line text-xl"></i>
            </div>
            {totalItems > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                {totalItems}
              </span>
            )}
          </button>
        </div>

        {/* Toast notification */}
        {addedMsg && (
          <div className="fixed bottom-24 right-6 z-30 bg-gray-900 text-white text-sm px-4 py-3 rounded-lg shadow-xl max-w-xs animate-pulse">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-check-line text-green-400"></i>
              </div>
              <span className="line-clamp-1">Added to cart!</span>
            </div>
          </div>
        )}

        <main className="flex-1">
          <StoreHero />
          <StoreProductGrid onAddToCart={addToCart} />
        </main>

        <CartDrawer
          isOpen={cartOpen}
          onClose={() => setCartOpen(false)}
          items={cart}
          onRemove={removeFromCart}
          onQuantityChange={changeQty}
          paymentLink={PAYMENT_LINK}
        />

        <Footer />
      </div>
    </>
  );
};

export default StorePage;
